import { useRef, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { compressImage } from '../lib/compressImage';
import type { CardDraft } from '../types';
import { ProductImage } from './ProductImage';

type Props = {
  card: CardDraft;
  index: number;
  total: number;
  onMove: (index: number, direction: -1 | 1) => void;
  onRemove: (index: number) => void;
  onChange: (index: number, patch: Partial<CardDraft>) => void;
};

export function CardRow({ card, index, total, onMove, onRemove, onChange }: Props) {
  const [editingImage, setEditingImage] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const needsAttention = card.missing.length > 0;

  const applyImage = (raw: string) => {
    const value = raw.trim();
    onChange(index, {
      imageUrl: value || null,
      // Подпись прокси умеет ставить только сервер — до сохранения показываем
      // картинку напрямую, после сохранения прокси подставится сам.
      imageProxyUrl: null,
    });
  };

  /** Загрузка с телефона: искать прямую ссылку на картинку с мобильного мучительно. */
  const uploadFile = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const { file: payload } = await compressImage(file);
      applyImage(await api.uploadCardImage(payload));
      setEditingImage(false);
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : 'Не удалось загрузить картинку');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className={`card-row ${needsAttention ? 'card-row--warn' : ''}`}>
      <button
        className="card-row__media"
        onClick={() => setEditingImage((v) => !v)}
        aria-label={card.imageUrl ? 'Заменить фото' : 'Добавить фото'}
      >
        {card.imageUrl ? (
          <ProductImage imageUrl={card.imageUrl} proxyUrl={card.imageProxyUrl} alt={card.title} />
        ) : (
          <span className="card-row__add-photo">+ фото</span>
        )}
      </button>

      <div className="card-row__body">
        {/* Крестик очищает поле в одно касание: парсер часто приносит длинный
            мусорный заголовок, стирать его вручную с телефона мучительно. */}
        <ClearableInput
          className="input--title"
          value={card.title}
          placeholder="Название товара"
          onChange={(value) => onChange(index, { title: value })}
        />
        <div className="card-row__meta">
          {/* Цены в витрине нет — вместо неё бренд, он показывается над названием. */}
          <ClearableInput
            className="input--brand"
            value={card.brand ?? ''}
            placeholder="Бренд"
            onChange={(value) => onChange(index, { brand: value.trim() || null })}
          />
          <span className="card-row__retailer">{card.retailer}</span>
        </div>

        {editingImage && (
          <div className="card-row__image-edit">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              hidden
              onChange={(e) => void uploadFile(e.target.files?.[0])}
            />
            <button
              className="card-row__upload"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Загружаю…' : '📷 Загрузить с устройства'}
            </button>

            <input
              className="input input--image"
              value={card.imageUrl?.startsWith('/') ? '' : (card.imageUrl ?? '')}
              onChange={(e) => applyImage(e.target.value)}
              placeholder="…или вставь ссылку https://"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
            />
            {card.imageUrl && (
              <button className="link-btn small" onClick={() => applyImage('')}>
                убрать фото
              </button>
            )}
            {uploadError && <p className="card-row__hint">{uploadError}</p>}
          </div>
        )}

        {needsAttention && (
          <div className="card-row__hint">
            Не распозналось: {card.missing.map(labelFor).join(', ')} — можно дописать вручную
          </div>
        )}
      </div>

      <div className="card-row__actions">
        <button
          className="icon-btn"
          onClick={() => onMove(index, -1)}
          disabled={index === 0}
          aria-label="Выше"
        >
          ↑
        </button>
        <button
          className="icon-btn"
          onClick={() => onMove(index, 1)}
          disabled={index === total - 1}
          aria-label="Ниже"
        >
          ↓
        </button>
        <button
          className="icon-btn icon-btn--danger"
          onClick={() => onRemove(index)}
          aria-label="Удалить"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

type ClearableProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  className: string;
};

/** Поле с крестиком очистки. Крестик занимает место внутри поля, не ломая ряд. */
function ClearableInput({ value, onChange, placeholder, className }: ClearableProps) {
  return (
    <span className={`clearable ${value ? 'clearable--filled' : ''}`}>
      <input
        className={`input ${className}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {value && (
        <button
          type="button"
          className="clearable__clear"
          /*
           * Действие на pointerdown, а не на click: при нажатии поле теряет
           * фокус, клавиатура на телефоне закрывается, вёрстка уезжает — и
           * браузер отменяет click, потому что палец поднялся уже не над
           * кнопкой. preventDefault оставляет фокус на месте.
           */
          onPointerDown={(e) => {
            e.preventDefault();
            onChange('');
          }}
          onClick={(e) => e.preventDefault()}
          aria-label={`Очистить: ${placeholder}`}
          tabIndex={-1}
        >
          ✕
        </button>
      )}
    </span>
  );
}

function labelFor(field: string): string {
  switch (field) {
    case 'title':
      return 'название';
    case 'imageUrl':
      return 'фото';
    case 'brand':
      return 'бренд';
    default:
      return field;
  }
}
